import type { DrapeSampler } from './alignment';

/**
 * Uniform-grid indexed point-in-triangle sampler over a non-indexed
 * triangle soup (9 numbers per triangle). Used to drape alignment
 * vertices onto a TIN. Build is O(triangles); each query touches only
 * the triangles whose bbox overlaps the query cell.
 *
 * The input is SNAPSHOT into an owned Float64Array at build time, so the
 * caller is free to mutate (or translate) the source buffer afterwards —
 * a live `BufferAttribute.array` handed in here stays safe when the
 * geometry is later re-positioned.
 */
export function buildTinSampler(positions: ArrayLike<number>, index?: ArrayLike<number>): DrapeSampler {
  const pos = Float64Array.from(positions);
  // Indexed input shares vertices between triangles (3 index entries per
  // triangle); without an index the buffer is a triangle soup (9 numbers per
  // triangle). `corner` hides the difference from everything below.
  const idx = index ? Uint32Array.from(index) : null;
  const triCount = idx ? Math.floor(idx.length / 3) : Math.floor(pos.length / 9);
  /** Offset into `pos` of corner `k` (0..2) of triangle `t`. */
  const corner = idx ? (t: number, k: number) => idx[t * 3 + k] * 3 : (t: number, k: number) => t * 9 + k * 3;
  if (triCount === 0) return () => null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < pos.length; i += 3) {
    const x = pos[i], y = pos[i + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const w = Math.max(maxX - minX, 1e-6);
  const h = Math.max(maxY - minY, 1e-6);
  // ~2 triangles per cell on average.
  const cellsPerAxis = Math.max(1, Math.min(1024, Math.round(Math.sqrt(triCount / 2))));
  const cw = w / cellsPerAxis;
  const ch = h / cellsPerAxis;
  const cells: Int32Array[] = [];
  const lists: number[][] = new Array(cellsPerAxis * cellsPerAxis);
  const cellOf = (x: number, y: number) => {
    const cx = Math.min(cellsPerAxis - 1, Math.max(0, Math.floor((x - minX) / cw)));
    const cy = Math.min(cellsPerAxis - 1, Math.max(0, Math.floor((y - minY) / ch)));
    return { cx, cy };
  };
  for (let t = 0; t < triCount; t++) {
    const o0 = corner(t, 0);
    const o1 = corner(t, 1);
    const o2 = corner(t, 2);
    const xs = [pos[o0], pos[o1], pos[o2]];
    const ys = [pos[o0 + 1], pos[o1 + 1], pos[o2 + 1]];
    const a = cellOf(Math.min(...xs), Math.min(...ys));
    const b = cellOf(Math.max(...xs), Math.max(...ys));
    for (let cy = a.cy; cy <= b.cy; cy++)
      for (let cx = a.cx; cx <= b.cx; cx++) {
        const idx = cy * cellsPerAxis + cx;
        (lists[idx] ??= []).push(t);
      }
  }
  for (let i = 0; i < lists.length; i++) cells[i] = lists[i] ? Int32Array.from(lists[i]) : new Int32Array(0);

  return (x, y) => {
    if (x < minX || x > maxX || y < minY || y > maxY) return null;
    const { cx, cy } = cellOf(x, y);
    const tris = cells[cy * cellsPerAxis + cx];
    for (let k = 0; k < tris.length; k++) {
      const o0 = corner(tris[k], 0);
      const o1 = corner(tris[k], 1);
      const o2 = corner(tris[k], 2);
      const x0 = pos[o0], y0 = pos[o0 + 1], z0 = pos[o0 + 2];
      const x1 = pos[o1], y1 = pos[o1 + 1], z1 = pos[o1 + 2];
      const x2 = pos[o2], y2 = pos[o2 + 1], z2 = pos[o2 + 2];
      const det = (y1 - y2) * (x0 - x2) + (x2 - x1) * (y0 - y2);
      if (Math.abs(det) < 1e-12) continue;
      const l0 = ((y1 - y2) * (x - x2) + (x2 - x1) * (y - y2)) / det;
      const l1 = ((y2 - y0) * (x - x2) + (x0 - x2) * (y - y2)) / det;
      const l2 = 1 - l0 - l1;
      const e = -1e-9;
      if (l0 >= e && l1 >= e && l2 >= e) return l0 * z0 + l1 * z1 + l2 * z2;
    }
    return null;
  };
}

export function combineSamplers(samplers: DrapeSampler[]): DrapeSampler {
  return (x, y) => {
    for (const s of samplers) {
      const z = s(x, y);
      if (z !== null) return z;
    }
    return null;
  };
}
